import { useState } from 'react'
import type { ReactNode } from 'react'
import {
  Alert, Anchor, Badge, Button, Code, Group, Select, Stack, Skeleton as MSkeleton, Table, Text, TextInput, Tooltip,
} from '@mantine/core'
import {
  IconAlertTriangle, IconCloudOff, IconExternalLink, IconHeartbeat, IconPlugConnected, IconSearch, IconUserOff,
} from '@tabler/icons-react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  DetailShell, Breadcrumbs, TabStrip, ActionMenu, PanelGrid, SectionCard,
  MetaList, StatusBadge, EmptyState,
} from '@lighthouse/design'
import { RestrictedState } from '../components/access/RestrictedState'
import { CancelRunAction } from '../components/cancel/CancelRunAction'
import { runScopeText } from '../components/cancel/cancelFormat'
import { RerunAction } from '../components/etl-explorer/RerunAction'
import { RunHangfirePanel } from '../components/etl-explorer/RunHangfirePanel'
import { RunLevelsPanel } from '../components/etl-explorer/RunLevelsPanel'
import { RunPipelinePanel } from '../components/etl-explorer/RunPipelinePanel'
import { StageStrip } from '../components/etl-explorer/StageStrip'
import {
  chunksTransformedText, completionFlagText, coverageText, durationText, entityModules, liveStageCaption, outcomeDisplay,
  outcomeHint,
  recordedForAnotherEntity, requestedTargetText, requestedWindowText, staleExplanation, successRateText,
  watermarkLabel,
} from '../components/etl-explorer/runFormat'
import { useEtlJobCatalog } from '../hooks/useEtlJobs'
import { useEtlRun, useEtlRuns } from '../hooks/useEtlRuns'
import { useExecution } from '../hooks/useExecutions'
import { isApiError, problemDetailText } from '../services/apiError'
import { DETAIL_ADMIN_ONLY, NOT_MEASURED, agoLabel, errorKind, errorSummary, exactNumber, whenLabel } from '../utils/format'
import { errorTextView } from '../utils/haltDetail'
import { hangfireJobUrl } from '../utils/hangfireUrl'
import type { EtlRunDetail, EtlRunListItem, EtlRunQuery } from '../types/etlRuns'
import type { StaleRule } from '../types/etlProgress'
import type { RunTrigger } from '../types/observatory'

/**
 * ETL Explorer.
 *
 * Every number on this page was fabricated until the Etl.EtlRun / EtlEntityRun /
 * EtlRunStep tables existed: the only ETL history was EtlActivityLog, which is
 * one row per entity completion with no phase rows and two counters, so per-stage
 * timings and six of the eight record counters had nowhere to come from.
 *
 * It is now served from recorded run telemetry (api/observatory/runs, typed in
 * types/etlRuns.ts). The things still not measurable — data-quality/validation
 * outcomes, per-stage baselines, a stale row's end — come back null and render
 * as a dash rather than being defaulted to something reassuring. That
 * distinction is the whole reason the page carried a sample-data banner: this
 * looks like operations tooling, and someone will screenshot it and believe it.
 *
 * A run here is an ENTITY run. The pass it sat in is `executionId`, which
 * Execution History lists; the run key links there. Wording and every
 * rerun/resume decision live in components/etl-explorer/runFormat.ts, where
 * each is pinned by a test.
 *
 * "Cancel run ETL-…" sits beside Rerun while this entity is in flight. It cancels
 * the WHOLE pass, never one entity, so it is decided by the pass's own outcome and
 * says how many other entity loads it stops (ExplorerCancel).
 */

const TRIGGER_LABEL: Record<RunTrigger, string> = {
  schedule: 'Schedule',
  dependency: 'Dependency',
  manual: 'Manual run',
  api: 'API',
  recovery: 'Recovery',
}

/** Sent explicitly, so the "showing the newest N" caption is backed by the request rather than a server default. */
const LIST_LIMIT = 50
const HISTORY_LIMIT = 50

const SURFACE = 'The ETL Explorer'

export function EtlExplorerPage() {
  const { runId } = useParams<{ runId: string }>()

  return runId ? <RunDetail runId={runId} /> : <RunList />
}

// ---------------------------------------------------------------------------
// Shared pieces
// ---------------------------------------------------------------------------

/** The outcome badge. `pending`/unknown outcomes are a plain gray Badge — StatusRole has no neutral member. */
function OutcomeBadge({ outcome, staleRule }: { outcome: string; staleRule?: StaleRule | null }) {
  const display = outcomeDisplay(outcome)
  const hint = outcomeHint(outcome, staleRule ?? null)
  const badge = display.tone.kind === 'status'
    ? <StatusBadge status={display.tone.role} label={display.label} />
    : <Badge color="gray" variant="light" size="sm" radius="sm">{display.label}</Badge>

  if (!hint) return badge
  return (
    <Tooltip label={hint} multiline w={320} withArrow>
      <span style={{ display: 'inline-flex' }}>{badge}</span>
    </Tooltip>
  )
}

/**
 * A failed read, by kind — never an empty list. A 401, a 500 and an unreachable
 * API each say something different, and none of them says "no runs".
 */
function LoadError({ error, what, notFound }: { error: unknown; what: string; notFound?: ReactNode }) {
  const kind = errorKind(error)
  const summary = errorSummary(error)
  const detail = summary ? <Code style={{ overflowWrap: 'anywhere' }}>{summary}</Code> : undefined

  if (kind === 'forbidden') {
    return <RestrictedState surface={SURFACE} reason={isApiError(error) ? problemDetailText(error.body) : null} />
  }
  if (kind === 'not-found' && notFound) return <>{notFound}</>
  if (kind === 'unauthorized') {
    return (
      <EmptyState
        icon={<IconUserOff size={28} stroke={1.5} />}
        title="Not signed in"
        message={`The API did not accept this session's credentials, so no ${what} could be read. Sign in again.`}
        action={detail}
      />
    )
  }
  if (kind === 'network') {
    return (
      <EmptyState
        icon={<IconCloudOff size={28} stroke={1.5} />}
        title="The API could not be reached"
        message={`The request got no answer, so nothing is known about any ${what} — this is not the same as there being none.`}
        action={detail}
      />
    )
  }
  return (
    <EmptyState
      icon={<IconAlertTriangle size={28} stroke={1.5} />}
      title={`Could not load the ${what}`}
      message={`The run-telemetry endpoint returned an error. This is not the same as there being no ${what}.`}
      action={detail}
    />
  )
}

/** Shown over data already on screen when a later refresh failed, so an old answer is not read as current. */
function RefreshFailed({ updatedAt }: { updatedAt: number }) {
  return (
    <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={16} />} title="Could not refresh">
      Showing what was last loaded, {agoLabel(new Date(updatedAt).toISOString())}.
    </Alert>
  )
}

/** Records loaded, with the served caveat beside it whenever there is one. */
function RecordsCell({ run }: { run: EtlRunListItem }) {
  if (!run.recordsLoadedCaveat) return <>{exactNumber(run.recordsLoaded)}</>
  return (
    <Group gap={4} wrap="nowrap">
      {exactNumber(run.recordsLoaded)}
      <Tooltip label={run.recordsLoadedCaveat} multiline w={300} withArrow>
        <span style={{ display: 'inline-flex' }} aria-label={run.recordsLoadedCaveat}>
          <IconAlertTriangle size={12} color="var(--status-warning-fg)" />
        </span>
      </Tooltip>
    </Group>
  )
}

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

/**
 * Recent entity runs, filtered server-side.
 *
 * The filters live in the query string (?entity=&module=) so a filtered list
 * is a link somebody can send. Entity is applied on Enter, not per keystroke;
 * the module options are the catalogue's Entity jobs' modules as served — not a
 * list this app keeps.
 */
function RunList() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const entity = searchParams.get('entity')?.trim() ?? ''
  const module = searchParams.get('module')?.trim() ?? ''
  const filtered = entity !== '' || module !== ''

  // The draft follows the applied value whenever the URL changes under it (back,
  // a link, Clear), without an effect: it is only "ours" while it was typed
  // against the entity currently applied.
  const [draftState, setDraftState] = useState({ against: entity, value: entity })
  const draft = draftState.against === entity ? draftState.value : entity

  const { data: catalog, isLoading: catalogLoading, isError: catalogError } = useEtlJobCatalog()
  const modules = entityModules(catalog)
  // A module applied from a link is shown as applied even if the catalogue does
  // not list it — it is the reader's value, not an invented option.
  const moduleOptions = module && !modules.includes(module) ? [...modules, module] : modules

  const query: EtlRunQuery = { limit: LIST_LIMIT }
  if (entity) query.entityName = entity
  if (module) query.module = module
  const { data: runs, isLoading, isError, error, dataUpdatedAt } = useEtlRuns(query)

  const apply = (next: { entity?: string; module?: string }) => {
    const params = new URLSearchParams(searchParams)
    const nextEntity = (next.entity ?? entity).trim()
    const nextModule = next.module ?? module
    if (nextEntity) params.set('entity', nextEntity)
    else params.delete('entity')
    if (nextModule) params.set('module', nextModule)
    else params.delete('module')
    setSearchParams(params)
  }

  const clear = () => {
    setDraftState({ against: '', value: '' })
    setSearchParams(new URLSearchParams())
  }

  let body: ReactNode
  if (isError && !runs) {
    body = <LoadError error={error} what="runs" />
  } else if (isLoading || !runs) {
    body = <MSkeleton height={320} />
  } else if (runs.length === 0) {
    body = filtered ? (
      <EmptyState
        title="No runs match these filters"
        message="Nothing recorded for this entity or module is still retained."
        action={<Button size="xs" variant="default" onClick={clear}>Clear filters</Button>}
      />
    ) : (
      <EmptyState
        title="No ETL runs recorded yet"
        message="Runs appear here once the worker has executed one. Every ETL job is started by hand, from the Scheduler."
        action={<Button size="xs" variant="light" onClick={() => navigate('/jobs')}>Open the Scheduler</Button>}
      />
    )
  } else {
    body = (
      <Stack gap="xs">
        {isError && <RefreshFailed updatedAt={dataUpdatedAt} />}
        <Table.ScrollContainer minWidth={820}>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Entity</Table.Th>
                <Table.Th>Module</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Started</Table.Th>
                <Table.Th>Duration</Table.Th>
                <Table.Th>Records</Table.Th>
                <Table.Th>Run</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {runs.map((run) => (
                <Table.Tr
                  key={run.runId}
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/etl-explorer/${run.runId}`)}
                >
                  <Table.Td>
                    <Anchor
                      component="button"
                      type="button"
                      size="sm"
                      onClick={(event) => {
                        event.stopPropagation()
                        navigate(`/etl-explorer/${run.runId}`)
                      }}
                    >
                      {run.entityName}
                    </Anchor>
                  </Table.Td>
                  <Table.Td>{run.module || NOT_MEASURED}</Table.Td>
                  <Table.Td><OutcomeBadge outcome={run.outcome} /></Table.Td>
                  <Table.Td>{agoLabel(run.startedAt)}</Table.Td>
                  <Table.Td>{durationText(run)}</Table.Td>
                  <Table.Td><RecordsCell run={run} /></Table.Td>
                  <Table.Td>
                    <Anchor
                      component="button"
                      type="button"
                      size="sm"
                      ff="monospace"
                      title="Open the whole run in Execution History"
                      onClick={(event) => {
                        event.stopPropagation()
                        navigate(`/execution-history/${run.executionId}`)
                      }}
                    >
                      {run.runKey}
                    </Anchor>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
        {runs.length >= LIST_LIMIT && (
          <Text size="xs" c="dimmed">
            Showing the newest {LIST_LIMIT}. Filter by entity or module to reach older runs.
          </Text>
        )}
      </Stack>
    )
  }

  return (
    <Stack gap="md" pb="xl">
      <Breadcrumbs items={[{ label: 'Operations' }, { label: 'ETL Explorer' }]} />

      <SectionCard
        title="Recent entity runs"
        subtitle="Newest first. Each row is one entity's load; its run key opens the whole run it belonged to."
      >
        <Stack gap="md">
          <Group gap="sm" align="flex-end" wrap="wrap">
            <TextInput
              label="Entity"
              description="Exact name; press Enter to apply"
              placeholder="e.g. SupplierInvoice"
              leftSection={<IconSearch size={14} />}
              value={draft}
              onChange={(event) => setDraftState({ against: entity, value: event.currentTarget.value })}
              onKeyDown={(event) => {
                if (event.key === 'Enter') apply({ entity: draft })
              }}
              w={260}
            />
            <Select
              label="Module"
              description={catalogError && !catalog ? 'The job catalogue could not be loaded' : 'From the job catalogue'}
              placeholder={catalogLoading ? 'Loading modules…' : moduleOptions.length === 0 ? 'No modules served' : 'All modules'}
              data={moduleOptions}
              value={module || null}
              onChange={(value) => apply({ module: value ?? '' })}
              disabled={moduleOptions.length === 0}
              clearable
              w={220}
            />
            {filtered && (
              <Button size="xs" variant="subtle" onClick={clear}>Clear filters</Button>
            )}
          </Group>
          {body}
        </Stack>
      </SectionCard>
    </Stack>
  )
}

// ---------------------------------------------------------------------------
// Detail
// ---------------------------------------------------------------------------

interface DetailTab {
  value: string
  label: string
  disabled?: boolean
  disabledHint?: string
}

/**
 * The TabStrip's tabs array. The open tab is the `?tab=` query value when it
 * names an ENABLED entry here, else Overview — so a tab added to this array is
 * linkable with no other change (`?tab=pipeline` opens the Pipeline view, which
 * the action menu's 'Pipeline view' also switches to), and a disabled or unknown
 * value never opens a blank body.
 *
 * Logs and Dependencies keep their names but say what they are on open: Logs is
 * the Hangfire job that CREATED the run (no log store exists), Dependencies is
 * the run's load levels (no dependency graph is recorded).
 */
const DETAIL_TABS: DetailTab[] = [
  { value: 'overview', label: 'Overview' },
  { value: 'pipeline', label: 'Pipeline' },
  { value: 'details', label: 'Details' },
  { value: 'history', label: 'History' },
  { value: 'connections', label: 'Connections' },
  { value: 'logs', label: 'Logs' },
  { value: 'dependencies', label: 'Dependencies' },
]

function openTab(requested: string | null): string {
  return DETAIL_TABS.find((t) => t.value === requested && !t.disabled)?.value ?? 'overview'
}

function RunDetail({ runId }: { runId: string }) {
  const navigate = useNavigate()
  // In the query string, so "this run's history" is a link somebody can send.
  // Replaced, not pushed: switching tabs is not navigation worth a Back press.
  const [searchParams, setSearchParams] = useSearchParams()
  const tab = openTab(searchParams.get('tab'))
  const setTab = (next: string) => {
    const params = new URLSearchParams(searchParams)
    if (next === 'overview') params.delete('tab')
    else params.set('tab', next)
    setSearchParams(params, { replace: true })
  }
  const { data: run, isLoading, isError, error, dataUpdatedAt } = useEtlRun(runId)

  if (isError && !run) {
    return (
      <Stack gap="md" pb="xl">
        <Breadcrumbs
          items={[
            { label: 'Operations' },
            { label: 'ETL Explorer', onClick: () => navigate('/etl-explorer') },
            { label: runId },
          ]}
        />
        <LoadError
          error={error}
          what="run"
          notFound={
            <EmptyState
              title={`No run ${runId} — it may have passed its retention window`}
              action={
                <Button size="xs" variant="default" onClick={() => navigate('/etl-explorer')}>
                  Back to recent runs
                </Button>
              }
            />
          }
        />
      </Stack>
    )
  }

  if (isLoading || !run) {
    return (
      <Stack gap="md">
        <MSkeleton height={150} />
        <MSkeleton height={220} />
      </Stack>
    )
  }

  const hangfireUrl = run.hangfireJobId ? hangfireJobUrl(run.hangfireJobId) : null

  return (
    <Stack gap="md" pb="xl">
      <DetailShell
        breadcrumbs={
          <Breadcrumbs
            items={[
              { label: 'Operations' },
              { label: 'ETL Explorer', onClick: () => navigate('/etl-explorer') },
              { label: run.runKey, onClick: () => navigate(`/execution-history/${run.executionId}`) },
              { label: run.entityName },
            ]}
          />
        }
        title={run.entityName}
        badges={<OutcomeBadge outcome={run.outcome} staleRule={run.staleRule} />}
        eyebrow={run.runKey}
        actions={
          <>
            <RerunAction run={run} />
            {(run.outcome === 'running' || run.outcome === 'stale') && <ExplorerCancel run={run} />}
            {/* Only with a real address: a relative /hangfire href would resolve
                into this SPA's own origin (utils/hangfireUrl.ts). */}
            {hangfireUrl && (
              <Anchor href={hangfireUrl} target="_blank" rel="noopener noreferrer" size="sm">
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  Open creating job in Hangfire
                  <IconExternalLink size={14} />
                </span>
              </Anchor>
            )}
            <ActionMenu
              items={[
                { label: 'Copy run id', onClick: () => void navigator.clipboard?.writeText(run.runId) },
                { label: 'Open entity history', onClick: () => setTab('history') },
                { label: 'Pipeline view', onClick: () => setTab('pipeline') },
              ]}
            />
          </>
        }
        tabs={
          <TabStrip value={tab} onChange={setTab} tabs={DETAIL_TABS} />
        }
      >
        <Stack gap="lg">
          {isError && <RefreshFailed updatedAt={dataUpdatedAt} />}
          {run.outcome === 'stale' && <StaleBanner run={run} />}

          {/* One body per tab. Each renders only on its own tab, so a tab's
              queries (History's list, Dependencies' run rows) run only when it is open. */}
          {tab === 'overview' && <OverviewTab run={run} />}
          {tab === 'pipeline' && <RunPipelinePanel run={run} />}
          {tab === 'details' && <DetailsTab run={run} />}
          {tab === 'history' && <HistoryTab run={run} />}
          {tab === 'connections' && <ConnectionsTab run={run} />}
          {tab === 'logs' && <RunHangfirePanel run={run} />}
          {tab === 'dependencies' && (
            <RunLevelsPanel
              run={run}
              renderError={(levelsError) => (
                <LoadError
                  error={levelsError}
                  what="other entities in this run"
                  notFound={
                    <EmptyState
                      title={`Run ${run.runKey} is no longer recorded`}
                      message="It may have passed its retention window, so its other entities cannot be listed."
                    />
                  }
                />
              )}
            />
          )}
        </Stack>
      </DetailShell>
    </Stack>
  )
}

/**
 * "Cancel run ETL-…" for an entity load still in flight.
 *
 * A cancel stops the WHOLE run — every entity in it — so the action reads the run
 * itself (Execution History's detail: the same read, and the same cache entry, the
 * Dependencies tab uses), never this entity's row: whether it is still running or
 * stale is the run's outcome, and "including N other entity loads" is counted from
 * the run's entities. Mounted only while this entity is running or stale, so a
 * finished entity's page reads nothing more; until the run's detail arrives it
 * shows nothing.
 */
function ExplorerCancel({ run }: { run: EtlRunDetail }) {
  const { data: execution } = useExecution(run.executionId)
  if (!execution) return null
  return (
    <CancelRunAction
      runId={run.executionId}
      runKey={run.runKey}
      runOutcome={execution.outcome}
      scope={runScopeText(run.runKey, execution, true)}
    />
  )
}

function StaleBanner({ run }: { run: EtlRunDetail }) {
  return (
    <Alert color="yellow" variant="light" icon={<IconHeartbeat size={16} />} title="Stale — no heartbeat">
      {run.lastHeartbeatAt
        ? <>Last heartbeat {whenLabel(run.lastHeartbeatAt)}. </>
        : <>No heartbeat was recorded for this row. </>}
      {staleExplanation(run.staleRule)}
    </Alert>
  )
}

/** A Warnings or Errors flag, with why it is a dash when it is one. */
function FlagValue({ flag }: { flag: { value: string; note: string | null } }) {
  return flag.note ? <Explained value={flag.value} note={flag.note} /> : <>{flag.value}</>
}

/** A value with a dimmed line of explanation under it. */
function Explained({ value, note }: { value: ReactNode; note: ReactNode }) {
  return (
    <Stack gap={2}>
      <span>{value}</span>
      <Text size="xs" c="dimmed">{note}</Text>
    </Stack>
  )
}

function OverviewTab({ run }: { run: EtlRunDetail }) {
  const navigate = useNavigate()
  const caption = liveStageCaption(run)
  const outcome = run.outcome as string

  const errorTitle = outcome === 'failed'
    ? 'Failure'
    : outcome === 'skipped'
      ? 'Why it was skipped'
      : 'Last error recorded'

  // Withheld from a non-administrator when the run halted, or the text copies a halt's detail (D8): the server's flag
  // decides, and the card says so rather than vanishing.
  const lastError = errorTextView(run.lastError, run.lastErrorWithheld)

  const countersBasis = run.outcome === 'running'
    ? 'So far — the run is in progress.'
    : run.outcome === 'stale'
      ? 'As of the last heartbeat.'
      : null

  return (
    <Stack gap="lg">
      {run.stages.length > 0
        ? <StageStrip stages={run.stages} totalSeconds={run.totalDurationSeconds} caption={caption} />
        : caption && <Text size="xs" c="dimmed">{caption}</Text>}

      {lastError.kind !== 'none' && (
        <SectionCard title={errorTitle}>
          {lastError.kind === 'withheld' ? (
            <Text size="sm" c="dimmed" data-state="detail-withheld">
              {DETAIL_ADMIN_ONLY}
            </Text>
          ) : (
            <Text size="sm" c={outcome === 'failed' ? 'red' : undefined} style={{ whiteSpace: 'pre-wrap' }}>
              {lastError.text}
            </Text>
          )}
        </SectionCard>
      )}

      <PanelGrid>
        <SectionCard title="Execution Summary">
          <MetaList
            items={[
              { label: 'Status', value: <OutcomeBadge outcome={run.outcome} staleRule={run.staleRule} /> },
              { label: 'Started', value: whenLabel(run.startedAt) },
              { label: 'Ended', value: whenLabel(run.completedAt) },
              { label: 'Duration', value: durationText(run) },
              { label: 'Triggered by', value: TRIGGER_LABEL[run.trigger] ?? run.trigger },
              { label: 'Schedule', value: run.schedule ?? NOT_MEASURED },
              {
                label: 'Worker',
                value: run.worker
                  ? (
                    <Anchor component="button" type="button" size="sm" onClick={() => navigate('/workers')}>
                      {run.worker}
                    </Anchor>
                  )
                  : NOT_MEASURED,
              },
              { label: 'Environment', value: run.environment || NOT_MEASURED },
              {
                label: 'Run',
                value: (
                  <Anchor
                    component="button"
                    type="button"
                    size="sm"
                    ff="monospace"
                    onClick={() => navigate(`/execution-history/${run.executionId}`)}
                  >
                    {run.runKey}
                  </Anchor>
                ),
              },
            ]}
          />
        </SectionCard>

        <SectionCard title="Record Summary" subtitle={countersBasis ?? undefined}>
          <Stack gap="sm">
            <MetaList
              items={[
                { label: 'Extracted', value: exactNumber(run.records.extracted) },
                { label: 'Transformed', value: exactNumber(run.records.transformed) },
                { label: 'Rejected / filtered', value: exactNumber(run.records.rejected) },
                { label: 'Keys resolved', value: exactNumber(run.records.keysResolved) },
                { label: 'Keys unresolved', value: exactNumber(run.records.keysUnresolved) },
                { label: 'Inserted', value: exactNumber(run.records.inserted) },
                { label: 'Updated', value: exactNumber(run.records.updated) },
                { label: 'Deleted', value: exactNumber(run.records.deleted) },
              ]}
            />
            {run.records.caveat && (
              <Group gap={6} wrap="nowrap" align="flex-start">
                <IconAlertTriangle size={14} color="var(--status-warning-fg)" style={{ flexShrink: 0, marginTop: 2 }} />
                <Text size="xs" c="dimmed">{run.records.caveat}</Text>
              </Group>
            )}
          </Stack>
        </SectionCard>

        <SectionCard title="Execution Health">
          <MetaList
            items={[
              {
                label: 'Success rate',
                value: run.health.successRatePercent == null && run.health.successRateUnavailableReason
                  ? <Explained value={NOT_MEASURED} note={run.health.successRateUnavailableReason} />
                  : successRateText(run.health),
              },
              {
                label: 'Data quality',
                value: run.health.dataQuality
                  ? <StatusBadge status={run.health.dataQuality} />
                  : NOT_MEASURED,
              },
              {
                label: 'Validation',
                value: run.health.validation
                  ? <StatusBadge status={run.health.validation} />
                  : NOT_MEASURED,
              },
              { label: 'Retries', value: exactNumber(run.health.retries) },
              // Flags written at completion, not counts: Yes/No, or a dash with why on an unfinished row.
              { label: 'Warnings', value: <FlagValue flag={completionFlagText(run.health.warnings, run.outcome)} /> },
              { label: 'Errors', value: <FlagValue flag={completionFlagText(run.health.errors, run.outcome)} /> },
            ]}
          />
        </SectionCard>
      </PanelGrid>
    </Stack>
  )
}

function DetailsTab({ run }: { run: EtlRunDetail }) {
  const navigate = useNavigate()
  const hangfireUrl = run.hangfireJobId ? hangfireJobUrl(run.hangfireJobId) : null
  const requested = run.requested

  const hangfireValue = run.hangfireJobId == null
    ? <Explained value={NOT_MEASURED} note="Not recorded on this run." />
    : hangfireUrl
      ? (
        <Anchor href={hangfireUrl} target="_blank" rel="noopener noreferrer" size="sm" ff="monospace">
          {run.hangfireJobId} <IconExternalLink size={12} style={{ verticalAlign: 'middle' }} />
        </Anchor>
      )
      : <Explained value={<Text span ff="monospace" size="sm">{run.hangfireJobId}</Text>} note="Hangfire dashboard address not configured." />

  const requestedValue = requested
    ? (
      <Explained
        value={`${requestedTargetText(requested)} · ${requestedWindowText(requested)}`}
        note={
          <>
            {requested.basis}
            {recordedForAnotherEntity(run) && (
              <> Recorded for a {requested.entity} request: this entity ran inside a pass created for another
                entity, so this may not be the window it loaded.</>
            )}
          </>
        }
      />
    )
    : <Explained value={NOT_MEASURED} note="No window was recorded when this run was created." />

  const coverage = run.windowCoverage
  const coverageValue = coverage.share == null
    ? <Explained value={NOT_MEASURED} note={coverageText(coverage)} />
    : <Explained value={coverageText(coverage)} note={coverage.basis} />

  return (
    <PanelGrid>
      <SectionCard title="Progress">
        <MetaList
          items={[
            { label: 'Attempts', value: exactNumber(run.attemptCount) },
            { label: 'Last heartbeat', value: whenLabel(run.lastHeartbeatAt) },
            {
              label: 'Stale rule',
              value: `No heartbeat for ${run.staleRule.minutes} min${run.staleRule.published ? '' : ' (code default)'}`,
            },
            { label: 'Load level', value: String(run.levelNumber) },
            { label: 'Chunks transformed', value: chunksTransformedText(run) },
            { label: 'Window coverage', value: coverageValue },
          ]}
        />
      </SectionCard>

      <SectionCard title="Window and watermark">
        <MetaList
          items={[
            { label: 'Requested', value: requestedValue },
            { label: watermarkLabel(run.watermarkIsFromThisRun), value: run.watermark ?? NOT_MEASURED, mono: true },
          ]}
        />
      </SectionCard>

      <SectionCard title="Identifiers">
        <MetaList
          items={[
            {
              label: 'Run',
              value: (
                <Anchor
                  component="button"
                  type="button"
                  size="sm"
                  ff="monospace"
                  onClick={() => navigate(`/execution-history/${run.executionId}`)}
                >
                  {run.runKey}
                </Anchor>
              ),
            },
            { label: 'Entity run id', value: run.runId, mono: true },
            { label: 'Created by Hangfire job', value: hangfireValue },
          ]}
        />
      </SectionCard>
    </PanelGrid>
  )
}

/** Other runs of the same entity, as a plain list. Deliberately no averages: nothing here is a baseline. */
function HistoryTab({ run }: { run: EtlRunDetail }) {
  const navigate = useNavigate()
  const { data: runs, isLoading, isError, error, dataUpdatedAt } = useEtlRuns({
    entityName: run.entityName,
    limit: HISTORY_LIMIT,
  })

  let body: ReactNode
  if (isError && !runs) {
    body = <LoadError error={error} what="runs" />
  } else if (isLoading || !runs) {
    body = <MSkeleton height={200} />
  } else if (runs.length === 0) {
    // The endpoint matches the name exactly; this run itself should be listed,
    // so an empty answer is worth saying plainly rather than hiding.
    body = <EmptyState title={`No runs of ${run.entityName} are listed`} message="Not even this one — it may have just passed its retention window." />
  } else {
    body = (
      <Stack gap="xs">
        {isError && <RefreshFailed updatedAt={dataUpdatedAt} />}
        <Table.ScrollContainer minWidth={640}>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Status</Table.Th>
                <Table.Th>Started</Table.Th>
                <Table.Th>Duration</Table.Th>
                <Table.Th>Records</Table.Th>
                <Table.Th>Run</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {runs.map((other) => {
                const isThis = other.runId === run.runId
                return (
                  <Table.Tr
                    key={other.runId}
                    style={{ cursor: isThis ? undefined : 'pointer' }}
                    onClick={isThis ? undefined : () => navigate(`/etl-explorer/${other.runId}`)}
                    aria-current={isThis || undefined}
                  >
                    <Table.Td><OutcomeBadge outcome={other.outcome} /></Table.Td>
                    <Table.Td>
                      {isThis
                        ? <Text size="sm" fw={600}>{agoLabel(other.startedAt)} (this run)</Text>
                        : agoLabel(other.startedAt)}
                    </Table.Td>
                    <Table.Td>{durationText(other)}</Table.Td>
                    <Table.Td><RecordsCell run={other} /></Table.Td>
                    <Table.Td>
                      <Text size="sm" ff="monospace">{other.runKey}</Text>
                    </Table.Td>
                  </Table.Tr>
                )
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
        {runs.length >= HISTORY_LIMIT && (
          <Text size="xs" c="dimmed">Showing the newest {HISTORY_LIMIT}.</Text>
        )}
      </Stack>
    )
  }

  return (
    <SectionCard title={`Runs of ${run.entityName}`} subtitle="Prior runs of this entity. Not a baseline.">
      {body}
    </SectionCard>
  )
}

/** The pivot into the connection ledger, which takes this same entity run id (ConnectionsPage ?runId=). */
function ConnectionsTab({ run }: { run: EtlRunDetail }) {
  const navigate = useNavigate()
  return (
    <SectionCard title="Source connections">
      <Stack gap="sm" align="flex-start">
        <Text size="sm">
          Each source connection this entity run&apos;s chunks opened — every attempt, retries included — is kept
          in the connection ledger for thirty days.
        </Text>
        <Button
          size="xs"
          variant="light"
          leftSection={<IconPlugConnected size={14} />}
          onClick={() => navigate(`/connections?runId=${encodeURIComponent(run.runId)}`)}
        >
          Open this run&apos;s connections
        </Button>
      </Stack>
    </SectionCard>
  )
}
